import React from "react";
import { useSearchParams } from "react-router-dom";
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Card from 'react-bootstrap/Card';
import DealerInfo from "./DealerInfo";
import InventorySelection from "./InventorySelection";
import { toast } from 'react-toastify';
import useApi from "../../../lib/apiService";
import { useDealerContext } from "../DealerContext.jsx";

function DealerLookup(props) {
    const Api = useApi();
    const context = useDealerContext();
    const [ewsDealer, setEwsDealer] = React.useState(null);
    const [corgData, setCorgData] = React.useState(null);
    const [cviConfigData, setCviConfigData] = React.useState(null);
    const [corgLoading, setCorgLoading] = React.useState(false);
    const [searchParams] = useSearchParams();
    // Get the addDealerToRecents function from props instead of creating a new state
    const addDealerToRecents = props.addDealerToRecents;
    
    const searchTerm = searchParams.get("dealerId");
    let entityHasBeenSet = searchParams.get("e");
    let cvi2HasBeenSet = searchParams.get("co");
    let ddcHasBeenSet = searchParams.get("ddc");
    let vinHasBeenSet = searchParams.get("vs");

    // CVI 2.1 inventory records are keyed on the Common Org id. Both the EWS and the CORG
    // branch below learn it, so whichever resolves first selects the dropdown and the other
    // must not override it.
    const selectCvi2IfUnset = (commonOrgId) => {
        if (commonOrgId && !cvi2HasBeenSet) {
            context.setCvi2(commonOrgId);
            cvi2HasBeenSet = commonOrgId;
        }
    };

    // Helper function to update UI with dealer data
    const updateUIWithDealerData = (dealer, source) => {
        if (source === 'ews') {
            setEwsDealer(dealer);
            document.title = dealer.name;
            if (dealer.platformId && !entityHasBeenSet) {
                context.setEntity(dealer.platformId);
                entityHasBeenSet = dealer.platformId;
            }
            if (dealer.id) {
                context.setVa(dealer.id);
            }
            selectCvi2IfUnset(dealer.commonOrgId);
            
            // Add to recent dealers list if we have a function to do so
            if (addDealerToRecents && dealer.name) {
                addDealerToRecents({
                    // The id will be overridden with the search term in the addDealerToRecents function
                    name: dealer.name,
                    logicalId: dealer.logicalId
                });
            }
        } else if (source === 'corg') {
            setCorgData(dealer);
            document.title = dealer.legalName || document.title;

            // Preselect dropdowns based on what the CORG lookup matched on
            const matchedOn = dealer.matchedOn || {};

            selectCvi2IfUnset(dealer.commonOrgId);

            // If matched on a platformId, prefer that specific one; otherwise use first available
            if (!entityHasBeenSet) {
                const matchedPlatformId = matchedOn.platformId;
                if (matchedPlatformId && dealer.platformIds?.includes(matchedPlatformId)) {
                    context.setEntity(matchedPlatformId);
                    entityHasBeenSet = matchedPlatformId;
                } else if (dealer.platformIds && dealer.platformIds.length > 0) {
                    context.setEntity(dealer.platformIds[0]);
                    entityHasBeenSet = dealer.platformIds[0];
                }
            }

            // Preselect DDC dropdown if matched on ddcId
            const preselectedDdcId = matchedOn.ddcId || (dealer.ddcIds && dealer.ddcIds.length === 1 && dealer.ddcIds[0]);
            if (!ddcHasBeenSet && preselectedDdcId) {
                context.setDdc(preselectedDdcId);
                ddcHasBeenSet = preselectedDdcId;
            }

            // Preselect VinSolutions dropdown if matched on vinId
            const preselectedVinId = matchedOn.vinId || (dealer.vinIds && dealer.vinIds.length === 1 && dealer.vinIds[0]);
            if (!vinHasBeenSet && preselectedVinId) {
                context.setVin(preselectedVinId);
                vinHasBeenSet = preselectedVinId;
            }
            
            // Add CORG dealer to recents if no EWS dealer was found
            if (!ewsDealer && addDealerToRecents && dealer.legalName) {
                addDealerToRecents({
                    // The id will be overridden with the search term in the addDealerToRecents function
                    name: dealer.legalName
                });
            }
        } else if (source === 'cvi_config') {
            setCviConfigData(dealer);
            document.title = `Entity ${dealer.entityId}`;
            if (dealer.entityId && !entityHasBeenSet) {
                context.setEntity(dealer.entityId);
                entityHasBeenSet = dealer.entityId;
            }
        }
    };

    // Fetch CORG data using the provided ID
    const fetchCorgData = async (id) => {
        setCorgLoading(true);
        try {
            const corgData = await Api.corgLookup(id);
            console.log(`CORG lookup result for ${id}:`, corgData);
            if (corgData) {
                updateUIWithDealerData(corgData, 'corg');
                return corgData;
            }
            return null;
        } catch (error) {
            console.error("Error loading CORG data:", error);
            return null;
        } finally {
            setCorgLoading(false);
        }
    };

    React.useEffect(() => {
        if (!searchTerm) {
            document.title = "Vehicle Data Explorer";
            return;
        }

        console.log("looking up dealer", searchTerm);
        setEwsDealer(null);
        setCorgData(null);
        setCviConfigData(null);
        
        const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(searchTerm);
        
        const lookupDealer = async () => {
            // Start EWS lookup
            const ewsPromise = Api.ewsLookup(searchTerm);
            
            // If it's a UUID, start CORG lookup in parallel
            let corgPromise = isUuid ? Api.corgLookup(searchTerm) : null;
            
            try {
                // Try EWS lookup first
                const ewsDealer = await ewsPromise;
                console.log("EWS lookup result:", ewsDealer);
                
                if (ewsDealer) {
                    // Found in EWS - update UI
                    updateUIWithDealerData(ewsDealer, 'ews');
                    
                    // If we have a commonOrgId but didn't start a CORG lookup, do it now
                    if (ewsDealer.commonOrgId && !corgPromise) {
                        await fetchCorgData(ewsDealer.commonOrgId);
                    }
                    
                    // If we started a CORG lookup in parallel, wait for it to complete
                    if (corgPromise) {
                        try {
                            const corgDealer = await corgPromise;
                            console.log("CORG lookup result (parallel):", corgDealer);
                            
                            if (corgDealer) {
                                updateUIWithDealerData(corgDealer, 'corg');
                            }
                        } catch (corgError) {
                            console.error("CORG lookup failed (parallel):", corgError);
                        }
                    }
                    return;
                }
                
                // EWS lookup failed, try CORG if not already started
                if (!corgPromise) {
                    console.log("EWS returned null, trying CORG lookup");
                    const corgData = await fetchCorgData(searchTerm);
                    if (corgData) {
                        return;
                    }
                }
            } catch (ewsError) {
                console.error("EWS lookup failed:", ewsError);
            }
            
            // If we've started a CORG lookup in parallel, wait for it to complete
            if (corgPromise) {
                try {
                    const corgDealer = await corgPromise;
                    console.log("CORG lookup result:", corgDealer);
                    
                    if (corgDealer) {
                        updateUIWithDealerData(corgDealer, 'corg');
                        return;
                    }
                } catch (corgError) {
                    console.error("CORG lookup failed:", corgError);
                }
            }
            
            // If we reach here, both EWS and CORG lookups failed, try CVI config as fallback
            /*
            console.log("EWS and CORG lookups failed, trying CVI config lookup as fallback");
            try {
                const cviConfigData = await Api.cviConfigLookup(searchTerm);
                console.log("CVI config lookup result:", cviConfigData);
                
                if (cviConfigData) {
                    updateUIWithDealerData(cviConfigData, 'cvi_config');
                    return;
                }
            } catch (cviError) {
                console.error("CVI config lookup failed:", cviError);
            }
            */
            // If we reach here, all lookups failed
            toast.error("Dealer not found", {autoClose: 2000});
        };
        
        lookupDealer();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchTerm]);
  
    return (
        <Container fluid style={{marginTop: "8px"}}>    
            {searchTerm && (ewsDealer || corgData || cviConfigData || corgLoading) && (
                <Card className="mb-3">
                    <Card.Body className="p-3">
                        <Row>
                            <Col md={6}>
                                <DealerInfo 
                                    ewsDealer={ewsDealer} 
                                    corgData={corgData} 
                                    cviConfigData={cviConfigData}
                                    corgLoading={corgLoading} 
                                />
                            </Col>
                            <Col md={6}>           
                                <InventorySelection 
                                    ewsDealer={ewsDealer} 
                                    corgData={corgData} 
                                    cviConfigData={cviConfigData}
                                    corgLoading={corgLoading} 
                                />
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>
            )}
        </Container>
    );
}

export default DealerLookup;